// AC-940 / S1.1 — Header dropdown menu component + interaction harness.
//
// A header nav link that carries `children` renders as a dropdown: a trigger (label + caret) that
// reveals its child links. Built on Fluent v9 `Menu`, so keyboard nav, ARIA (aria-haspopup /
// aria-expanded, role=menu / menuitem), Escape + outside-click dismiss, and collision-aware
// positioning come for free; we add hover-intent (openOnHover + hoverDelay). Recursion is capped at
// two levels (a child may open one submenu; deeper `children` are ignored).
//
// Standalone by design (AC-941 wires it into NavBar). Nothing here imports NavBar or the router —
// navigation for in-app links goes through the `onNavigate` callback the caller supplies.

import React from 'react';
import {
    Menu, MenuTrigger, MenuButton, MenuPopover, MenuList, MenuItem, MenuItemLink,
} from '@fluentui/react-components';
import { IHeaderLink, isExternalUrl, isNonNavigableUrl } from '../Helpers/headerLinksConfig';

// How long the pointer must dwell before a hover opens/closes the menu, so a diagonal path toward
// the panel doesn't flicker it shut. Fluent applies this symmetrically to open and close.
const HOVER_DELAY_MS = 150;
const MAX_DEPTH = 2;

export interface HeaderDropdownProps {
    link: IHeaderLink;                       // the parent link (expected to have children or a panel)
    onNavigate: (url: string) => void;       // in-app navigation for internal, same-tab links
    trigger?: 'hover' | 'click';             // default: hover (Fluent still supports click/keyboard)
    triggerClassName?: string;               // e.g. 'hc-link' so the trigger matches sibling links
    triggerStyle?: React.CSSProperties;
    itemClassName?: string;                  // e.g. 'hc-menu-item'
    itemStyle?: React.CSSProperties;
    menuClassName?: string;                  // e.g. 'sf-menu-popover'
    // Surface for the plain children dropdown and its nested submenus (background/border/radius).
    // Fluent's default popover surface is white, which is unreadable under a dark header once the
    // caller derives the item text color from the header's luminance — so NavBar always supplies
    // this. Mega-menu panels use panelStyle instead.
    menuStyle?: React.CSSProperties;
    // AC-960 / S5.1 — a rich mega-menu panel (pre-rendered ILayout). When provided, the popover shows
    // this instead of the children menu list; a panel supersedes a plain children dropdown.
    panel?: React.ReactNode;
    // AC-972 — background/border/shadow for the mega-menu panel surface (both the full-width bar and
    // the popover mega). Computed by NavBar: luminance-aware default (matches the header/theme) with an
    // optional explicit override, so a dark-themed store doesn't get a hard-coded white slab.
    panelStyle?: React.CSSProperties;
}

// One child link rendered as a Fluent menu entry: external / new-tab / tel: / mailto: are plain
// anchors (MenuItemLink); internal same-tab links route via onNavigate. Shared by the desktop
// dropdown (HeaderDropdown) and the mobile accordion (HeaderMenuAccordionItem).
function renderLeafMenuItem(
    link: IHeaderLink,
    key: React.Key,
    onNavigate: (url: string) => void,
    itemClassName?: string,
    itemStyle?: React.CSSProperties,
): React.ReactElement {
    const external = isExternalUrl(link.url);
    const nonNavigable = isNonNavigableUrl(link.url);
    if (external || link.newTab) {
        const openInNewTab = link.newTab && !nonNavigable;
        return (
            <MenuItemLink
                key={key}
                className={itemClassName}
                style={itemStyle}
                href={link.url || '#'}
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noopener noreferrer' : undefined}
            >
                {linkInner(link, true)}
            </MenuItemLink>
        );
    }
    return (
        <MenuItem key={key} className={itemClassName} style={itemStyle} onClick={() => { if (link.url) onNavigate(link.url); }}>
            {linkInner(link, true)}
        </MenuItem>
    );
}

const linkInner = (link: IHeaderLink, iconSizedForMenu: boolean) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em', whiteSpace: 'nowrap' }}>
        {link.iconUrl && (
            <img
                src={link.iconUrl}
                alt=""
                className={iconSizedForMenu ? undefined : 'hc-link-icon'}
                style={iconSizedForMenu ? { height: '1.2em', width: 'auto', maxWidth: '1.5em', objectFit: 'contain', flex: '0 0 auto' } : undefined}
            />
        )}
        {link.label && <span>{link.label}</span>}
    </span>
);

// AC-968 — a mega-menu panel rendered as a full-width bar dropping just below the header (spanning
// the viewport, content centered), rather than a popover anchored under the trigger. Hand-rolled
// (Fluent's popover positioning can't span the viewport): hover-intent open/close, Escape + outside
// click dismiss, aria-haspopup/expanded on the trigger. The `top` is measured from the trigger so it
// tracks the actual header height (sticky or not).
const FullWidthMega: React.FC<{
    link: IHeaderLink;
    panel: React.ReactNode;
    triggerClassName?: string;
    triggerStyle?: React.CSSProperties;
    openOnHover: boolean;
    panelStyle?: React.CSSProperties;
}> = ({ link, panel, triggerClassName, triggerStyle, openOnHover, panelStyle }) => {
    const [open, setOpen] = React.useState(false);
    const [top, setTop] = React.useState(0);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const closeTimer = React.useRef<ReturnType<typeof setTimeout>>();

    const doOpen = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        if (triggerRef.current) setTop(Math.round(triggerRef.current.getBoundingClientRect().bottom));
        setOpen(true);
    };
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), HOVER_DELAY_MS);
    };

    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onDown);
        return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onDown); };
    }, [open]);

    return (
        <span
            className="hc-mega"
            onMouseEnter={openOnHover ? doOpen : undefined}
            onMouseLeave={openOnHover ? scheduleClose : undefined}
            style={{ display: 'inline-flex' }}
        >
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => (open ? setOpen(false) : doOpen())}
                style={{ background: 'none', border: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '0.3em', ...triggerStyle }}
            >
                {linkInner(link, false)}
                <span aria-hidden="true" style={{ fontSize: '0.6em', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}>&#9660;</span>
            </button>
            {open && (
                <div
                    ref={panelRef}
                    role="region"
                    aria-label={link.label}
                    onMouseEnter={openOnHover ? doOpen : undefined}
                    onMouseLeave={openOnHover ? scheduleClose : undefined}
                    className="hc-mega-panel"
                    style={{ position: 'fixed', left: 0, right: 0, top, zIndex: 300, background: '#fff', borderTop: '1px solid #e6ebf2', boxShadow: '0 18px 40px rgba(16,46,85,.12)', ...panelStyle }}
                >
                    <div style={{ maxWidth: 1240, margin: '0 auto' }}>{panel}</div>
                </div>
            )}
        </span>
    );
};

const HeaderDropdown: React.FC<HeaderDropdownProps> = (props) => {
    const { link, onNavigate, trigger = 'hover', triggerClassName, triggerStyle, itemClassName, itemStyle, menuClassName, menuStyle, panel, panelStyle } = props;
    const children = (link.children ?? []).filter((c) => (c.label && c.label.trim()) || c.iconUrl);
    // Render if there's a panel or at least one child; a bare link is handled by the caller.
    if (children.length === 0 && !panel) return null;

    // AC-968 — full-width mega bar (opt-in via link.panelFullWidth) bypasses the Fluent popover.
    if (panel && link.panelFullWidth) {
        return <FullWidthMega link={link} panel={panel} triggerClassName={triggerClassName} triggerStyle={triggerStyle} openOnHover={trigger !== 'click'} panelStyle={panelStyle} />;
    }

    // One child entry. A child that itself has children (and is within the depth cap) opens a nested
    // submenu; otherwise it's a leaf link. External / new-tab / tel: / mailto: links are plain anchors
    // (MenuItemLink); internal same-tab links route via onNavigate.
    const renderItem = (child: IHeaderLink, i: number, depth: number): React.ReactNode => {
        const grandchildren = (child.children ?? []).filter((c) => (c.label && c.label.trim()) || c.iconUrl);
        const key = child.id ?? `${depth}-${i}`;

        if (grandchildren.length > 0 && depth < MAX_DEPTH) {
            return (
                <Menu key={key} openOnHover={trigger !== 'click'} hoverDelay={HOVER_DELAY_MS} positioning="after-top">
                    <MenuTrigger disableButtonEnhancement>
                        <MenuItem className={itemClassName} style={itemStyle}>{linkInner(child, true)}</MenuItem>
                    </MenuTrigger>
                    {/* A nested submenu is its own popover, so it needs the same surface as the
                        parent dropdown — otherwise level 2 reverts to Fluent's white. */}
                    <MenuPopover className={menuClassName} style={menuStyle}>
                        <MenuList>
                            {grandchildren.map((gc, gi) => renderItem(gc, gi, depth + 1))}
                        </MenuList>
                    </MenuPopover>
                </Menu>
            );
        }

        return renderLeafMenuItem(child, key, onNavigate, itemClassName, itemStyle);
    };

    return (
        <Menu openOnHover={trigger !== 'click'} hoverDelay={HOVER_DELAY_MS} positioning="below-start">
            <MenuTrigger>
                {/* MenuButton gives us aria-haspopup + aria-expanded + a built-in chevron; transparent
                    appearance so it blends with the sibling header links (S1.2 tunes exact styling). */}
                <MenuButton appearance="transparent" className={triggerClassName} style={{ color: 'inherit', font: 'inherit', ...triggerStyle }}>
                    {linkInner(link, false)}
                </MenuButton>
            </MenuTrigger>
            {/* AC-972 — for a paneled (mega) popover, override Fluent's default white surface with the
                luminance-aware background + readable text so a dark-themed header doesn't get a white slab.
                A plain children dropdown gets the same treatment via menuStyle, which the caller derives
                from the header menu / bar luminance (its items are colored the same way). */}
            <MenuPopover
                className={panel ? undefined : menuClassName}
                style={panel
                    ? (panelStyle ? { background: panelStyle.background as string, color: panelStyle.color as string } : undefined)
                    : menuStyle}
            >
                {panel
                    ? <div className="hc-megamenu">{panel}</div>
                    : <MenuList>{children.map((c, i) => renderItem(c, i, 1))}</MenuList>}
            </MenuPopover>
        </Menu>
    );
};

// AC-942 / S1.3 — mobile accordion. In the collapsed "More" / auto menu (narrow screens, where there
// is no hover), a nested link renders as a tap-to-expand accordion — its children appear inline below
// the trigger — instead of a hover flyout. The toggle is a plain button (role=menuitem) so tapping it
// does NOT dismiss the surrounding Fluent menu; the children are real MenuItems that navigate and
// close on select. Two-level cap (a child's own children aren't expanded here).
export const HeaderMenuAccordionItem: React.FC<{
    link: IHeaderLink;
    onNavigate: (url: string) => void;
    itemClassName?: string;
    itemStyle?: React.CSSProperties;
    // AC-962 / S5.3 — a mega-menu panel (pre-rendered ILayout). On narrow/touch there's no hover, so
    // an expanded paneled link shows the panel content inline (stacked) rather than a flyout; a panel
    // supersedes the plain children list, mirroring desktop.
    panel?: React.ReactNode;
}> = ({ link, onNavigate, itemClassName, itemStyle, panel }) => {
    const [open, setOpen] = React.useState(false);
    const children = (link.children ?? []).filter((c) => (c.label && c.label.trim()) || c.iconUrl);
    if (children.length === 0 && !panel) {
        return renderLeafMenuItem(link, link.id ?? 'leaf', onNavigate, itemClassName, itemStyle);
    }
    return (
        <>
            <button
                type="button"
                role="menuitem"
                aria-expanded={open}
                className={itemClassName}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '0.5em', background: 'none', border: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left', ...itemStyle }}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            >
                {linkInner(link, true)}
                <span aria-hidden="true" style={{ fontSize: '0.7em', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}>&#9662;</span>
            </button>
            {open && (
                <div role="group" style={{ paddingLeft: '1em' }}>
                    {panel
                        ? panel
                        : children.map((c, i) => renderLeafMenuItem(c, c.id ?? `acc-${i}`, onNavigate, itemClassName, itemStyle))}
                </div>
            )}
        </>
    );
};

export default HeaderDropdown;
